import { properties as propertiesApi } from '../api';
import { createPageHeader } from '../components/form';
import { createDataTable, type Column } from '../components/table';
import { createStatCard } from '../components/card';
import { navigate } from '../router';
import type { Property } from '../types';

function fmt(n: number): string {
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 }).format(n);
}

function daysUntil(dateStr: string): number {
  if (!dateStr) return Infinity;
  const d = new Date(dateStr);
  const now = new Date();
  return Math.ceil((d.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
}

function renewalBadge(dateStr: string): string {
  if (!dateStr) return '<span class="badge badge-neutral">Not Set</span>';
  const days = daysUntil(dateStr);
  if (days < 0) return `<span class="badge badge-danger">Expired</span>`;
  if (days <= 30) return `<span class="badge badge-warning">${days}d left</span>`;
  if (days <= 90) return `<span class="badge badge-info">${days}d left</span>`;
  return `<span class="badge badge-success">${days}d left</span>`;
}

export async function insurancePage(): Promise<HTMLElement> {
  const page = document.createElement('div');
  page.appendChild(createPageHeader('Insurance'));

  let propList: Property[] = [];
  try {
    propList = await propertiesApi.list();
  } catch {
    page.innerHTML += '<div class="text-red-500 p-4">Failed to load properties.</div>';
    return page;
  }

  // Summary stats
  const withInsurance = propList.filter(p => p.insurance_company);
  const totalCost = propList.reduce((sum, p) => sum + (p.insurance_annual_cost || 0), 0);
  const expiringSoon = propList.filter(p => {
    const days = daysUntil(p.insurance_renewal_date);
    return days >= 0 && days <= 60;
  });
  const expired = propList.filter(p => {
    const days = daysUntil(p.insurance_renewal_date);
    return days < 0 && p.insurance_renewal_date;
  });

  const statsRow = document.createElement('div');
  statsRow.className = 'grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6';
  statsRow.appendChild(createStatCard('Insured', `${withInsurance.length}/${propList.length}`, 'properties'));
  statsRow.appendChild(createStatCard('Total Annual Cost', fmt(totalCost)));
  statsRow.appendChild(createStatCard('Expiring Soon', `${expiringSoon.length}`, '≤ 60 days', expiringSoon.length > 0 ? 'text-amber-600' : undefined));
  statsRow.appendChild(createStatCard('Expired', `${expired.length}`, undefined, expired.length > 0 ? 'text-red-600' : undefined));
  page.appendChild(statsRow);

  // Table
  const cols: Column<Property>[] = [
    {
      label: 'Property',
      render: p => `<a href="#/properties/${p.id}" class="text-primary-600 hover:underline font-medium">${p.address}, ${p.suburb}</a>`,
    },
    {
      label: 'Insurance Company',
      render: p => p.insurance_company || '<span class="text-gray-400">—</span>',
    },
    {
      label: 'Policy Number',
      render: p => p.insurance_policy_number || '<span class="text-gray-400">—</span>',
    },
    {
      label: 'Annual Cost',
      render: p => p.insurance_annual_cost ? fmt(p.insurance_annual_cost) : '<span class="text-gray-400">—</span>',
    },
    {
      label: 'Renewal Date',
      render: p => p.insurance_renewal_date
        ? `${p.insurance_renewal_date} ${renewalBadge(p.insurance_renewal_date)}`
        : '<span class="text-gray-400">Not set</span>',
    },
    {
      label: '',
      render: p => `<button class="btn-ghost text-sm edit-btn" data-id="${p.id}">Edit</button>`,
    },
  ];

  // Sort: expired first, then by renewal date ascending
  const sorted = [...propList].sort((a, b) => {
    const da = daysUntil(a.insurance_renewal_date);
    const db = daysUntil(b.insurance_renewal_date);
    // Properties without dates go to the end
    if (da === Infinity && db === Infinity) return 0;
    if (da === Infinity) return 1;
    if (db === Infinity) return -1;
    return da - db;
  });

  const table = createDataTable(cols, sorted, { emptyText: 'No properties found.' });
  page.appendChild(table);

  // Wire edit buttons to go to property edit page
  table.querySelectorAll('.edit-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const id = btn.getAttribute('data-id')!;
      navigate(`#/properties/${id}/edit`);
    });
  });

  return page;
}
